import { Injectable, Injector } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { PagingResponse } from '@models/paging-response.model';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { ODataRequest } from '../utilities/sdk-base/o-data-request';
import { Collection } from '../models/ui/collection.model';
import { CodingFieldResource } from '../models/resource/coding-field.resource';
import { CodingFieldResourceToEditableFieldMapper } from '../models/mappers/coding-field-resource-to-editable-field.mapper';
import { EditableFieldSection } from '../models/ui/editable-field-section.model';
import { EditableFieldNode } from '../models/ui/editable-field-node.model';
import { EditableFieldToCodingFieldResourceMapper } from '../models/mappers/editable-field-to-coding-field-resource.mapper';

@Injectable({
	providedIn: 'root'
})
export class CodingFieldSdk extends SdkBase {

	constructor(injector: Injector) {
		super(injector);
	}

	public get(): Observable<Collection<EditableFieldSection>> {
		const request = new ODataRequest<CodingFieldResource>('get', 'codingfields');
		request.$orderBy('rank', 'asc');
		request.$count();

		return this.makeODataRequest$<PagingResponse<CodingFieldResource>>(request, 'api2').pipe(
			map((response) => {
				const mapper = new CodingFieldResourceToEditableFieldMapper();
				const editableFields = mapper.mapCollection(response.value);
				const sectionList = (editableFields
					.filter((field) => field instanceof EditableFieldSection) as EditableFieldSection[])
					.map((section) => {
						section.items = editableFields
							.filter((field) => field instanceof EditableFieldNode && field.parentId === section.id) as EditableFieldNode[];
						return section;
					});
				return new Collection<EditableFieldSection>({
					items: sectionList
				});
			})
		);
	}

	public put(params: PutParams): Observable<EditableFieldNode | EditableFieldSection> {
		if (params?.field) {
			const editableFieldToCodingFieldResourceMapper = new EditableFieldToCodingFieldResourceMapper();
			const body = editableFieldToCodingFieldResourceMapper.map(params.field);
			const request = new ODataRequest('put', 'codingfields', params.field.id, body);
			return this.makeODataRequest$<CodingFieldResource>(request, 'api2').pipe(
				map((response) => {
					const codingFieldResourceToEditableFieldMapper = new CodingFieldResourceToEditableFieldMapper();
					return codingFieldResourceToEditableFieldMapper.map(response);
				})
			);
		}
		return of(null);
	}

	public delete(params: DeleteParams): Observable<void> {
		if (params?.field) {
			const request = new ODataRequest('delete', 'codingfields', params.field.id);
			return this.makeODataRequest$<void>(request, 'api2').pipe(
				map(() => null)
			);
		}
		return of(null);
	}

	importCodingFields(params: ImportCodingFieldsParams): Observable<void> {
		if (params?.file) {
			const formData = new FormData();
			formData.append('File', params.file, params.file.name);
			formData.append('FileType', `.${params.file.name.split('.').pop()}`);
			return this.makeHttpRequest('post', 'api2/codingfields/import', formData);
		}
		return of(null);
	}
}


interface PutParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface DeleteParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface ImportCodingFieldsParams {
	file: File;
}
